import React, { useEffect, useRef, useState } from "react";
import "./Homepage.css";

function MapModal({ onClose, onLocationSelect }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const userMarker = useRef(null);
  const googleMapsScriptTag = useRef(null);
  const scriptInjected = useRef(false);
  const [cityName, setCityName] = useState("");

  const fallbackLocation = { lat: 26.746, lng: 94.210 }; // Jorhat

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      return new Promise((resolve) => {
        if (window.google?.maps) return resolve();

        const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
        if (!key) {
          console.warn("Google Maps API key missing. Using fallback map.");
          return resolve();
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = () => {
          console.error("Google Maps failed to load. Using fallback.");
          resolve();
        };
        document.body.appendChild(script);
        googleMapsScriptTag.current = script;
        scriptInjected.current = true;
      });
    };

    const initMap = (retry = 0) => {
      if (!mapRef.current) return;

      if (!window.google?.maps?.Map) {
        if (retry < 10) return setTimeout(() => initMap(retry + 1), 200);

        // Fallback map
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
          center: fallbackLocation,
          zoom: 7,
        });
        return;
      }

      const assamBounds = new window.google.maps.LatLngBounds(
        { lat: 24.0, lng: 89.6 },
        { lat: 28.0, lng: 97.5 }
      );

      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: fallbackLocation,
        zoom: 7,
        restriction: { latLngBounds: assamBounds, strictBounds: true },
      });

      mapInstance.current.addListener("click", (e) => {
        const latLng = e.latLng;
        const mapsLink = `https://www.google.com/maps?q=${latLng.lat()},${latLng.lng()}`;
        onLocationSelect(mapsLink);
        handleClose();
      });
    };

    loadGoogleMapsScript().then(() => initMap());

    return () => {
      if (userMarker.current) userMarker.current.setMap(null);
      if (mapInstance.current && window.google?.maps)
        window.google.maps.event.clearInstanceListeners(mapInstance.current);

      if (scriptInjected.current && googleMapsScriptTag.current) {
        document.body.removeChild(googleMapsScriptTag.current);
      }

      mapInstance.current = null;
      googleMapsScriptTag.current = null;
      userMarker.current = null;
    };
  }, [onLocationSelect]);

const goToMyLocation = () => {
  if (!mapInstance.current) return;

  if (!navigator.geolocation) {
    alert("Geolocation is not supported by your browser");
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const userLatLng = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      console.log("User location:", userLatLng);
      mapInstance.current.panTo(userLatLng);
      mapInstance.current.setZoom(14);

      if (userMarker.current) userMarker.current.setMap(null);
      userMarker.current = new window.google.maps.Marker({
        position: userLatLng,
        map: mapInstance.current,
        icon: { url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png" },
        title: "You are here",
      });
    },
    (err) => {
      console.error("Geolocation failed:", err);
      const fallback = { lat: 26.746, lng: 94.210 }; // Jorhat
      mapInstance.current.panTo(fallback);
      mapInstance.current.setZoom(12);

      if (userMarker.current) userMarker.current.setMap(null);
      userMarker.current = new window.google.maps.Marker({
        position: fallback,
        map: mapInstance.current,
        icon: { url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png" },
        title: "Fallback Location (Jorhat)",
      });

      let msg = "";
      switch (err.code) {
        case 1:
          msg = "Permission denied. Please allow location access.";
          break;
        case 2:
          msg = "Position unavailable. Could not detect your location.";
          break;
        case 3:
          msg = "Timeout. Could not detect your location in time.";
          break;
        default:
          msg = "Unknown error. Showing fallback location.";
      }
      alert(msg);
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );
};


  const goToCity = async () => {
    if (!mapInstance.current || !cityName) return;

    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
    if (!key) return alert("Google Maps API key missing");

    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(cityName)}&key=${key}`
      );
      const data = await res.json();
      const loc = data.results?.[0]?.geometry?.location;
      if (loc) {
        mapInstance.current.setCenter(loc);
        mapInstance.current.setZoom(12);
      } else {
        alert("City not found");
      }
    } catch (err) {
      console.error(err);
      alert("Error fetching city location");
    }
  };

  const handleClose = () => {
    if (userMarker.current) userMarker.current.setMap(null);
    if (mapInstance.current && window.google?.maps)
      window.google.maps.event.clearInstanceListeners(mapInstance.current);

    if (scriptInjected.current && googleMapsScriptTag.current) {
      document.body.removeChild(googleMapsScriptTag.current);
    }

    mapInstance.current = null;
    userMarker.current = null;
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <input
            type="text"
            value={cityName}
            onChange={(e) => setCityName(e.target.value)}
            placeholder="Enter City Name"
            style={{ flex: 1, padding: 8, borderRadius: 6, border: "1px solid #ccc", fontSize: 16 }}
          />
          <button onClick={goToCity} style={{ fontSize: 20 }}>
            🔍
          </button>
        </div>
        <div ref={mapRef} style={{ width: "100%", height: 500, borderRadius: 10 }} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
          <button onClick={goToMyLocation}>📍 My Location</button>
          <button onClick={handleClose}>❌ Close</button>
        </div>
      </div>
    </div>
  );
}

export default MapModal;
